import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { hasObstructionSpans } from "@CommonUtils/pog/shelfMerchandising";
import { PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { usePogAtomActions } from "src/components/Planogram/store/actions";
import {
  planogramSelectedShelfAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Button, Color, Flex, Select, SmallNumberInput, Text } from "src/elements";
import { SelectOption } from "src/elements/Select/store/types";
import { useTranslation } from "src/i18n";
import { DIMENSION } from "src/utils/formatDimension";
import { usePlanogramPanel } from "../../../store/hooks";

interface Props {
  shelf: PlanogramShelf & { isNew?: boolean };
  pogAtom: PrimitiveAtom<Pog>;
}

export const EditShelf = ({ shelf, pogAtom }: Props) => {
  const {
    deleteShelf,
    setShelfDepth,
    setShelfMaxFrontOverhang,
    setShelfThickness,
    setShelfOffset,
    setShelfNotchNo,
    setShelfMerchandisableWidth,
    setShelvesType,
    moveShelfToBayNo,
    addNewShelf,
  } = usePogAtomActions(pogAtom);

  const pog = useAtomValue(pogAtom);
  const [selectedShelf, setSelectedShelf] = useAtom(planogramSelectedShelfAtom);
  const setSelectedShelves = useSetAtom(planogramSelectedShelvesAtom);
  const { setTab } = usePlanogramPanel();
  const { t } = useTranslation("common");
  const shelfTypeOptions = useMemo<SelectOption<PlanogramShelf["type"]>[]>(
    () => [
      { value: "REGULAR", label: t("shelfTypes.regular") },
      { value: "RISER", label: t("shelfTypes.riser") },
      { value: "HANGCELL", label: t("shelfTypes.hangCell") },
    ],
    [t],
  );

  useEffect(() => {
    if (selectedShelf) {
      const updatedShelf = pog.getShelf(selectedShelf.uniqueId);
      if (updatedShelf && selectedShelf.isNew) {
        // `isNew` is UI-only, so preserve it when planogram edits clone the shelf model.
        Object.assign(updatedShelf, { isNew: true });
      }
      setSelectedShelf(updatedShelf);
    }
  }, [pog]);

  const deleteNewShelf = () => {
    setSelectedShelf(null);
    setSelectedShelves([]);

    if (selectedShelf?.isNew) {
      deleteShelf(shelf);
    }
  };

  const saveNewShelf = () => {
    setSelectedShelf(null);
    setSelectedShelves([]);
  };

  const startAddingNewFixture = () => {
    const newShelf = addNewShelf(1);
    setSelectedShelf(newShelf);
    setSelectedShelves(newShelf ? [newShelf.uniqueId] : []);
    setTab("ADD_EDIT_FIXTURE");
  };

  // Merged shelves must stay fully merchandisable (the merge invariant), so — matching the
  // existing pattern for other width-affecting edits — the field is disabled rather than
  // silently unmerging or leaving an invalid merged state. An obstruction sets the width of
  // the shelves it blocks, so those are disabled too.
  const isMerchandisableWidthDisabled =
    Boolean(shelf.mergedLeft || shelf.mergedRight) || hasObstructionSpans(shelf);
  const physicalWidthValue = shelf.width / DIMENSION.INCH;
  const merchandisableWidthValue = (shelf.merchandisableWidth ?? shelf.width) / DIMENSION.INCH;

  return (
    <Flex column flexGrow role="form" aria-label={t("shelfEditor.label")}>
      <Flex width="100%" gap="10px" padding="10px" column flexGrow>
        <Text variant="small1" color={Color.primary}>
          Shelf: {shelf.getName()}
        </Text>

        {shelf.isNew ? (
          <Select
            title={t("shelfTypes.label")}
            size="small"
            options={shelfTypeOptions}
            value={shelf.type}
            setValue={(type) => setShelvesType([shelf], type)}
          />
        ) : null}

        <SmallNumberInput
          label="Bay:"
          variant="sidebuttons"
          textVariant="small1"
          textColor={Color.textMain}
          width="20px"
          value={shelf.bay.getNo()}
          setValue={(bayNo) => moveShelfToBayNo(shelf, bayNo)}
          min={1}
          max={pog.planogram.bays.length}
        />

        <SmallNumberInput
          label="Notch:"
          variant="sidebuttons"
          textVariant="small1"
          textColor={Color.textMain}
          width="20px"
          value={shelf.getNotchNo()}
          setValue={(notchNo) => setShelfNotchNo(shelf, notchNo)}
          min={1}
          max={shelf.bay.maxNotch}
        />

        <SmallNumberInput
          label="Depth:"
          value={shelf.depth / DIMENSION.INCH}
          setValue={(depth) => setShelfDepth(shelf, depth * DIMENSION.INCH)}
          textVariant="small1"
          textColor={Color.textMain}
        />

        <SmallNumberInput
          label={t("frontOverhang.editShelf.label")}
          value={(shelf.maxFrontOverhang ?? 0) / DIMENSION.INCH}
          setValue={(value) => setShelfMaxFrontOverhang(shelf, value * DIMENSION.INCH)}
          min={0}
          textVariant="small1"
          textColor={Color.textMain}
          isDisabled={
            shelf.type !== "REGULAR" ||
            Boolean(shelf.slope || shelf.mergedLeft || shelf.mergedRight)
          }
        />
        <Text variant="small1" color={Color.textMain}>
          {t("frontOverhang.editShelf.help")}
        </Text>

        <SmallNumberInput
          label="Thickness:"
          value={shelf.thickness / DIMENSION.INCH}
          setValue={(thickness) => setShelfThickness(shelf, thickness * DIMENSION.INCH)}
          textVariant="small1"
          textColor={Color.textMain}
        />

        <SmallNumberInput
          label="Offset:"
          value={shelf.offset / DIMENSION.INCH}
          setValue={(offset) => setShelfOffset(shelf, offset * DIMENSION.INCH)}
          textVariant="small1"
          textColor={Color.textMain}
        />

        <SmallNumberInput
          label="Merchandisable Width:"
          value={merchandisableWidthValue}
          setValue={(value) =>
            setShelfMerchandisableWidth(shelf, Math.round(value * DIMENSION.INCH))
          }
          min={0}
          max={physicalWidthValue}
          textVariant="small1"
          textColor={Color.textMain}
          isDisabled={isMerchandisableWidthDisabled}
        />

        <SmallNumberInput
          label="Shelf Width:"
          value={physicalWidthValue}
          setValue={() => undefined}
          textVariant="small1"
          textColor={Color.textMain}
          isDisabled
        />
      </Flex>

      {selectedShelf?.isNew ? (
        <Flex padding="10px" width="100%" justify="between">
          <Button variant="inverted" color="red" onClick={deleteNewShelf}>
            Cancel
          </Button>

          <Button color="primary" onClick={saveNewShelf}>
            Save
          </Button>
        </Flex>
      ) : (
        <Flex padding="10px">
          <Button width="100%" variant="inverted" onClick={startAddingNewFixture}>
            Add New Shelf
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
